/**
 * @file tableAnnotatorDb.reducer.js
 * @description
 *
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 *
 * @author don michael
 * @since Dec 03, 2022 15:06
 */
import * as Actions from 'src/store/actions/tableAnnotator/tableAnnotatorDb.actions';
import { arrayDeepCopy, arrayExists, objectDeepCopy } from 'src/@common/utilities';

import { clearKey, saveKey } from 'src/@common/sessionStorage';

function newSearchResult() {
    return {
        queryCount: 0,
        pageSize: 0,
        pageCount: 0,
        pageNo: 0,
        queryType: '',
        pageDocs: [],  // session annotations
        message: ''
    };
}

const initialState = {
    // annotation session parameters
    annotatorId: null,
    tableId: null,

    // redirections
    redirectToTableAnnotator: false,

    // session annotations for the tableId + genBatchId
    searchResult: newSearchResult(),

    // a couple of table properties used as metadata
    // when displaying generated annotations
    tableMetadata: {},

    // note: biocurations for the table + publication or supplement
    // are stored in the documentAnnatatorDb.reducer to 
    // support editing annotations within the documentAnnotator
    // biocurations: {},

    dataLoaded: false,

    // batch id of the latest generation operation
    genBatchId: null,

    // array subset of schema.supportedFields
    // containing fields designated for this classificationCode
    schemaSupportedFields: [],

    schemaClassifications: [],

    // fields for a single classificationCode
    // formated for use as options in an autoCompleteField
    schemaFieldOptions: [],

    // table being annotated; contains table rows
    table: {},

    // server status
    error: null,
    serverErrorExists: false,
    serverFieldErrors: {},  // field errors from the server, passed into the form for display
    serverMessage: '', // message from server for display in toaster message
};

const tableAnnotatorDbReducer = (state = initialState, action) => {

    let colIndex = null;
    let curatedTableCheckedColIndex = null;
    let curatedTableGroupNoColIndex = null;
    let fieldsRow = null;
    let foundIndex = null;
    let foundRow = null;
    let globalFields = null;
    let groupsRow = null;
    let rowId = null;
    let rowIndex = null;
    let tableRows = null;
    let tmpObj = null;
    let tmpOptions = null;
    let tmpTable = null;
    let value = null;

    switch (action.type) {

        // tableAnnotations.setters - used mostly for development
        // -------------------------------------------------------------------
        case Actions.TABLE_ANNOTATOR_ANNOTATOR_ID_SET:
            return {
                ...state,
                annotatorId: action.payload,
            };

        case Actions.TABLE_ANNOTATOR_TABLE_ID_SET:
            return {
                ...state,
                tableId: action.payload,
            };

        case Actions.TABLE_ANNOTATOR_GENBATCHID_SET: {
            return {
                ...state,
                genBatchId: action.payload,
            };
        }

        case Actions.REDIRECT_TO_TABLE_ANNOTATOR_SET: {
            return {
                ...state,
                redirectToTableAnnotator: action.payload,
            };
        }

        // tableAnnotator.resets
        // -------------------------------------------------------------------
        case Actions.TABLE_ANNOTATOR_DB_RESET:
            // resets everything
            clearKey('genBatchId');

            return {
                ...state,
                redirectToTableAnnotator: false,
                searchResult: newSearchResult(),
                dataLoaded: false,
                schemaSupportedFields: [],
                schemaClassifications: [],
                schemaFieldOptions: [],
                genBatchId: null,
                table: {},
                error: null,
                serverErrorExists: false,
                serverFieldErrors: {},
                serverMessage: '',
            };

        case Actions.TABLE_ANNOTATOR_DB_CLEAR:
            // resets just past generation session.
            // leaves table and annotator intact
            clearKey('genBatchId');

            tmpTable = objectDeepCopy(state.table);

            for (let index = 0; index < tmpTable.rowsObj.fieldsRow.length; index++) {
                tmpTable.rowsObj.fieldsRow[index] = [];
            }

            for (let index = 0; index < tmpTable.rowsObj.groupsRow.length; index++) {
                tmpTable.rowsObj.groupsRow[index] = '';
            }

            for (let index = 0; index < tmpTable.rowsObj.dataRows.length; index++) {
                tmpTable.rowsObj.dataRows[index][tmpTable.rowsObj.selectedColIndex] = false;
            }

            return {
                ...state,
                searchResult: newSearchResult(),
                schemaSupportedFields: [],
                schemaClassifications: [],
                schemaFieldOptions: [],
                table: tmpTable,
                genBatchId: null,
                error: null,
                serverErrorExists: false,
                serverFieldErrors: {},
                serverMessage: '',
            };

        // tableAnnotator.annotateTable
        // -------------------------------------------------------------------
        case Actions.TABLE_ANNOTATOR_TABLE_ANNOTATE_REQUEST:
            return {
                ...state,
                annotatorId: action.payload.annotatorId,
                tableId: action.payload.tableId,
                dataLoaded: false,
                searchResult: newSearchResult(),
                table: {},
                redirectToTableAnnotator: true,
            };

        case Actions.TABLE_ANNOTATOR_TABLE_ANNOTATE_SUCCESS:
            // note: biocurations are captured by the
            // documentAnnotatorDb.reducer for use in 
            // editing annotations
            return {
                ...state,
                dataLoaded: true,
                table: action.payload.table,
            };

        case Actions.TABLE_ANNOTATOR_TABLE_ANNOTATE_FAILURE:
            return {
                ...state,
                error: action.payload,
            };

        // tableAnnotator.curatedTable
        // -------------------------------------------------------------------
        case Actions.TABLE_ANNOTATOR_CURATED_TABLE_CHECKED_ROW_SET:
            curatedTableCheckedColIndex = state.table.rowsObj.selectedColIndex;
            value = action.payload.value;
            rowIndex = action.payload.rowIndex;

            tmpTable = objectDeepCopy(state.table);
            tableRows = tmpTable.rowsObj.dataRows;
            tableRows[rowIndex][curatedTableCheckedColIndex] = value;

            return {
                ...state,
                table: tmpTable,
            };

        case Actions.TABLE_ANNOTATOR_CURATED_TABLE_CHECK_ALL_SET:
            curatedTableCheckedColIndex = state.table.rowsObj.selectedColIndex;
            value = action.payload;

            tmpTable = objectDeepCopy(state.table);
            tableRows = tmpTable.rowsObj.dataRows;

            // skip the first row, which is usually the column labels
            for (let itor = 1; itor < tableRows.length; itor++) {
                tableRows[itor][curatedTableCheckedColIndex] = value;
            }

            return {
                ...state,
                table: tmpTable,
            };

        case Actions.TABLE_ANNOTATOR_CURATED_TABLE_GROUPNO_ROW_SET:
            curatedTableGroupNoColIndex = state.table.rowsObj.groupNoColIndex;
            value = action.payload.value;
            rowIndex = action.payload.rowIndex;

            tmpTable = objectDeepCopy(state.table);
            tableRows = tmpTable.rowsObj.dataRows;
            tableRows[rowIndex][curatedTableGroupNoColIndex] = value;

            return {
                ...state,
                table: tmpTable,
            };

        case Actions.TABLE_ANNOTATOR_CURATED_TABLE_FIELD_SET:
            // sets value of a single fields header column
            colIndex = action.payload.colIndex;
            value = action.payload.value;

            tmpTable = objectDeepCopy(state.table);
            fieldsRow = tmpTable.rowsObj.fieldsRow;

            fieldsRow[colIndex] = value;

            return {
                ...state,
                table: tmpTable,
            };

        case Actions.TABLE_ANNOTATOR_CURATED_TABLE_FIELDS_ROW_SET:
            // sets values for all columns in the fieldsRow
            tmpTable = objectDeepCopy(state.table);
            tmpTable.rowsObj.fieldsRow = action.payload;

            return {
                ...state,
                table: tmpTable,
            };

        case Actions.TABLE_ANNOTATOR_CURATED_TABLE_GROUPNO_COLUMN_SET:
            // sets value of single groupNo header column
            colIndex = action.payload.colIndex;
            value = action.payload.value;

            tmpTable = objectDeepCopy(state.table);
            groupsRow = tmpTable.rowsObj.groupsRow;

            groupsRow[colIndex] = value;

            return {
                ...state,
                table: tmpTable,
            };

        case Actions.TABLE_ANNOTATOR_CURATED_TABLE_DISABLED_OPTIONS_SET:
            // finds all fieldNames that have been used in fieldsRow
            // and globalFields and marks those schemaFieldOptions as 'disabled',
            // except for the internalNote field, which can be reused
            tmpOptions = arrayDeepCopy(state.schemaFieldOptions);

            fieldsRow = action.payload.fieldsRow;
            globalFields = action.payload.globalFields;

            // reset
            tmpOptions.forEach((option) => {
                option.disabled = false;
            });

            if (arrayExists(tmpOptions)) {

                if (fieldsRow) {
                    fieldsRow.forEach((col) => {

                        if (arrayExists(col)) {

                            col.forEach((colField) => {

                                if (colField !== 'internalField') {
                                    foundIndex = tmpOptions.findIndex((item) => {
                                        return item.value === colField;
                                    });

                                    if (foundIndex !== -1) {
                                        tmpOptions[foundIndex].disabled = true;
                                    }
                                }

                            });
                        }

                    });
                }

                if (globalFields) {
                    for (const key in globalFields) {
                        foundIndex = tmpOptions.findIndex((item) => {
                            return item.value === key;
                        });

                        if (foundIndex !== -1) {
                            tmpOptions[foundIndex].disabled = true;
                        }
                    }
                }
            }

            return {
                ...state,
                schemaFieldOptions: tmpOptions,
            };

        // tableAnnotator.submitAnnotationType
        // -------------------------------------------------------------------
        case Actions.TABLE_ANNOTATOR_SUBMIT_ANNOTATION_TYPE_REQUEST:
            return {
                ...state,
                schemaClassifications: [],
            };

        case Actions.TABLE_ANNOTATOR_SUBMIT_ANNOTATION_TYPE_SUCCESS:
            return {
                ...state,
                schemaClassifications: action.payload.schemaClassifications,
            };

        case Actions.TABLE_ANNOTATOR_SUBMIT_ANNOTATION_TYPE_FAILURE:
            return {
                ...state,
                error: action.payload,
            };

        // tableAnnotator.submitClassificationCode
        // -------------------------------------------------------------------
        case Actions.TABLE_ANNOTATOR_SUBMIT_CLASSIFICATION_CODE_REQUEST:
            return {
                ...state,
                schemaFieldOptions: [],
                schemaSupportedFields: [],
            };

        case Actions.TABLE_ANNOTATOR_SUBMIT_CLASSIFICATION_CODE_SUCCESS:
            return {
                ...state,
                schemaFieldOptions: action.payload.schemaFieldOptions,
                schemaSupportedFields: action.payload.schemaSupportedFields,
            };

        case Actions.TABLE_ANNOTATOR_SUBMIT_CLASSIFICATION_CODE_FAILURE:
            return {
                ...state,
                error: action.payload,
            };

        // tableAnnotator.submitTableSettings
        // -------------------------------------------------------------------
        case Actions.TABLE_ANNOTATOR_SUBMIT_TABLE_SETTINGS_REQUEST:
            return {
                ...state,
            };

        case Actions.TABLE_ANNOTATOR_SUBMIT_TABLE_SETTINGS_SUCCESS:
            return {
                ...state,
            };

        case Actions.TABLE_ANNOTATOR_SUBMIT_TABLE_SETTINGS_FAILURE:
            return {
                ...state,
                error: action.payload,
            };

        // tableAnnotator.generateAnnotations
        // -------------------------------------------------------------------
        case Actions.TABLE_ANNOTATOR_GENERATE_ANNOTATIONS_REQUEST:
            clearKey('genBatchId');

            return {
                ...state,
                genBatchId: null,
                searchResult: newSearchResult(),
            };

        case Actions.TABLE_ANNOTATOR_GENERATE_ANNOTATIONS_SUCCESS:
            // insert into sessionStorage to survive a page refresh
            saveKey('genBatchId', action.payload.genBatchId);

            return {
                ...state,
                dataLoaded: true,
                genBatchId: action.payload.genBatchId,
                searchResult: action.payload.queryResult,
                serverErrorExists: action.payload.errorExists,
                serverMessage: action.payload.serverMessage,
            };

        case Actions.TABLE_ANNOTATOR_GENERATE_ANNOTATIONS_FAILURE:
            return {
                ...state,
                error: action.payload,
            };

        // tableAnnotator.findTableAnnotations
        // -------------------------------------------------------------------
        case Actions.TABLE_ANNOTATOR_FIND_TABLE_ANNOTATIONS_REQUEST:
            clearKey('genBatchId');

            return {
                ...state,
                genBatchId: null,
                searchResult: newSearchResult(),
            };

        case Actions.TABLE_ANNOTATOR_FIND_TABLE_ANNOTATIONS_SUCCESS:
            // insert into sessionStorage to survive a page refresh
            saveKey('genBatchId', action.payload.genBatchId);

            return {
                ...state,
                dataLoaded: true,
                genBatchId: action.payload.genBatchId,
                tableMetadata: action.payload.tableMetadata,
                searchResult: action.payload.queryResult,
                serverErrorExists: action.payload.errorExists,
                serverMessage: action.payload.serverMessage,
            };

        case Actions.TABLE_ANNOTATOR_FIND_TABLE_ANNOTATIONS_FAILURE:
            return {
                ...state,
                error: action.payload,
            };

        // generated.annotations.pageQuery - requests subsequent pages of annotations.
        // note: the first page of annotations is delivered in the response
        // to: TABLE_ANNOTATOR_GENERATE_ANNOTATIONS_SUCCESS
        // -------------------------------------------------------------------
        case Actions.TABLE_ANNOTATIONS_PAGE_QUERY_REQUEST:
            return {
                ...state,
                searchResult: newSearchResult(),
                serverErrorExists: false,
                serverMessage: '',
                dataLoaded: false,
            };

        case Actions.TABLE_ANNOTATIONS_PAGE_QUERY_SUCCESS:
            return {
                ...state,
                dataLoaded: true,
                searchResult: action.payload.queryResult,
                serverErrorExists: action.payload.errorExists,
                serverMessage: action.payload.serverMessage,
            };

        case Actions.TABLE_ANNOTATIONS_PAGE_QUERY_FAILURE:
            return {
                ...state,
                error: action.payload,
            };


        // tableAnnotator.tableAnnotations.delete
        // -------------------------------------------------------------------
        case Actions.TABLE_ANNOTATOR_TABLE_ANNOTATIONS_DELETE_REQUEST:
            return {
                ...state,
                searchResult: newSearchResult(),
                serverErrorExists: false,
                serverMessage: '',
                dataLoaded: false,
            };

        case Actions.TABLE_ANNOTATOR_TABLE_ANNOTATIONS_DELETE_SUCCESS:
            return {
                ...state,
                dataLoaded: true,
                searchResult: action.payload.queryResult,
                serverErrorExists: action.payload.errorExists,
                serverMessage: action.payload.serverMessage,
            };

        case Actions.TABLE_ANNOTATOR_TABLE_ANNOTATIONS_DELETE_FAILURE:
            return {
                ...state,
                error: action.payload,
            };

        // tableAnnotator.tableAnnotations.checked
        // -------------------------------------------------------------------
        case Actions.TABLE_ANNOTATIONS_CHECKED_ROW_SET:
            rowId = action.payload.rowId;
            value = action.payload.value;

            tmpObj = objectDeepCopy(state.searchResult);

            foundRow = tmpObj.pageDocs.find((row) => {
                return row._id === rowId;
            });

            if (foundRow) {
                foundRow.selected = value;
            }

            return {
                ...state,
                searchResult: tmpObj,
            };

        case Actions.TABLE_ANNOTATIONS_CHECKED_ALL_SET:
            value = action.payload;

            tmpObj = objectDeepCopy(state.searchResult);

            tmpObj.pageDocs.forEach((row) => {
                row.selected = value;
            })

            return {
                ...state,
                searchResult: tmpObj,
            };

        /************* */
        default: {
            return state;
        }
    }

};

export default tableAnnotatorDbReducer;